import React, { useMemo } from 'react';
import { ChakraProvider, Flex, Text, extendTheme } from '@chakra-ui/react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { formatDateWithTimezone } from 'ts/common/common';
import { LoadingIndicator } from 'ts/common/components';
import { Button } from 'ts/common/components/gallery';
import { getTheme } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { replaceHtmlEscapeCharacters } from 'ts/common/utils';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import ClientPageHeading from 'client_react/common/components/ClientPageHeading';
import ClientPageLayout from 'client_react/common/components/ClientPageLayout';
import { useClientApiFetch } from '../bootstrap';
import { FetchMethod } from '../bootstrap';

interface InvoiceError {
    detail?: string;
    status: number;
}

const isInvoiceError = (response: unknown): response is InvoiceError => {
    return (
        response !== null &&
        typeof response === 'object' &&
        'status' in response &&
        typeof (response as InvoiceError).status === 'number' &&
        (response as InvoiceError).status >= 400
    );
};

const InvoiceAppContent: React.FC = () => {
    const t = useTranslate('invoice');
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const { response: invoice, loading } = useClientApiFetch<SpApi.Client.IInvoice | InvoiceError>(
        `invoice/${invoiceId}`,
        {
            method: FetchMethod.GET
        }
    );

    const theme = useMemo(
        () =>
            invoice && !isInvoiceError(invoice) && invoice.brandTheme
                ? extendTheme(getTheme(invoice.brandTheme))
                : undefined,
        [invoice]
    );

    if (!theme) {
        return null;
    }

    if (loading) {
        return (
            <ChakraProvider resetCSS={false} theme={theme}>
                <LoadingIndicator />
            </ChakraProvider>
        );
    }

    if (!invoice) {
        return (
            <div>
                <h1>{t('invoiceNotFound')}</h1>
                <p>{t('invoiceNotFoundMessage')}</p>
            </div>
        );
    }

    if (isInvoiceError(invoice)) {
        return (
            <div>
                <h1>{t('errorLoadingInvoice')}</h1>
                <p>{t('unableToLoadInvoice')}</p>
            </div>
        );
    }

    const getNextInstallment = () => {
        // TODO: Make sure we're getting the next installment correctly
        return invoice.installments[0];
    };

    const getBannerSubtitle = () => {
        const nextInstallment = getNextInstallment();

        if (!nextInstallment) {
            // TODO: Show an appropriate subtitle when there are no installments
            return undefined;
        }

        const dueDate = formatDateWithTimezone(nextInstallment.dueDate);

        // TODO: Show an appropriate subtitle when the payment is overdue

        return replaceHtmlEscapeCharacters(
            t('nextPaymentIsDueOnDate', {
                currencySymbol: invoice.currencySymbol,
                amount: nextInstallment.amount,
                dueDate: dueDate
            })
        );
    };

    const getBanner = () => {
        const bannerTitle = t('greeting', {
            firstName: invoice.contact.firstName
        });

        return <ClientPageHeading subtitle={getBannerSubtitle()}>{bannerTitle}</ClientPageHeading>;
    };

    const getHeadingRightContentText = () => {
        const nextInstallment = getNextInstallment();

        const daysUntilDueDate = Math.floor(
            (new Date(nextInstallment.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );

        if (daysUntilDueDate < 0) {
            return t('nextPaymentDueOverdue', {
                currencySymbol: invoice.currencySymbol,
                amount: nextInstallment.amount
            });
        }

        if (daysUntilDueDate === 0) {
            return t('nextPaymentDueToday', {
                currencySymbol: invoice.currencySymbol,
                amount: nextInstallment.amount
            });
        }

        if (daysUntilDueDate === 1) {
            return t('nextPaymentDueTomorrow', {
                currencySymbol: invoice.currencySymbol,
                amount: nextInstallment.amount
            });
        }

        return t('nextPaymentDueInSomeDays', {
            currencySymbol: invoice.currencySymbol,
            amount: nextInstallment.amount,
            days: daysUntilDueDate
        });
    };

    const openPaymentPage = () => {
        navigate(`/invoice/${invoice.publicId}/payment`);
    };

    const getHeadingRightContent = () => {
        const text = getHeadingRightContentText();
        const isLandingPage = !location.pathname.includes('/payment');

        return (
            <Flex alignItems="center" gap="12px">
                <Text fontSize="14px">{text}</Text>
                {isLandingPage && <Button onClick={openPaymentPage} text={t('payInvoice')} />}
            </Flex>
        );
    };

    // TODO: Remove this log
    console.log(invoice);

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            <ClientPageLayout
                banner={getBanner()}
                headingRightContent={getHeadingRightContent()}
                headingText={invoice.brandName}
            >
                <Outlet context={{ invoice }} />
            </ClientPageLayout>
        </ChakraProvider>
    );
};

const InvoiceApp: React.FC = withBootstrapData(() => {
    const { translations } = useBootstrapData();

    const PageWithTranslations = useMemo(
        () => withI18NextTranslate(InvoiceAppContent, translations),
        [translations]
    );

    return <PageWithTranslations />;
});

export default InvoiceApp;
